import { useEffect, useRef, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import { useKiosk } from '../state/store'
import { useScene } from '../scene/store'
import { OMLOOP, halklok } from '../magazijn/omloop'
import { zweefStatus } from '../magazijn/CameraRig'
import { PROEF } from '../magazijn/proef'
import { FILM_VERHOUDING, ZWEEFFILM, halZoalsFilm, useZweeffilm } from '../kiosk/zweeffilm'

/** Seconden vooruit zoeken in de film: de hal loopt door terwijl de film het beeld ophaalt. */
const VOORUIT = 0.6
/** Overvloeien van de hal naar de film, en terug. */
const NAAR_FILM_MS = 700
const NAAR_HAL_MS = 300
/** Na een film die bleef hangen (buffer leeg): zo lang de hal realtime. */
const BLOKKADE_MS = 60_000

/** Beelden die de hal getekend heeft, geteld binnen het canvas (`ZweeffilmBrug`). */
const hal = { beelden: 0 }

/** Telt de beelden van de hal: na de film weet de overname zo wanneer de hal er weer staat. */
export function ZweeffilmBrug() {
  useFrame(() => { hal.beelden++ })
  return null
}

type Fase = 'hal' | 'zoeken' | 'naarFilm' | 'film' | 'naarHal'

/**
 * De film van de zweefronde op het hoofdscherm (docs/zweeffilm.md). Een
 * video boven het canvas, onder de scrim en de tegels.
 *
 * - **Hal → film.** Staat de camera precies op de zweefroute
 *   (`zweefStatus.opRoute`), dan zoekt de film het moment `VOORUIT` s verder
 *   op de klok van de hal. Komt de hal daar aan, dan start de film, volgt de
 *   klok van de hal de film, en vloeit de film over de hal heen. Daarna staat
 *   de hal stil: geen beeld meer op de GPU zolang de film speelt.
 * - **Film → hal.** Bij een tik op een tegel gaat de hal weer tekenen, op de
 *   tijd van de film en met de camera op de route (`springNaarRoute`). Twee
 *   beelden later vloeit de film weg; pas dan begint de vlucht
 *   (`vasthouden`). Daarna telt de hal zijn klok weer zelf.
 *
 * Omdat alles in de hal rond is in de omloop (`omloop.ts`), is de hal op tijd
 * τ hetzelfde als beeld τ van de film. Wat verschilt is alleen de
 * beeldkwaliteit: daarvoor het overvloeien.
 *
 * Geen film — niet op de server, uit in de Instellingen of met `?film=0`, een
 * aangepaste scène in de Studio, een scherm dat geen 16:9 is — dan is dit
 * onderdeel er niet en is het hoofdscherm de hal zoals eerst.
 */
export function Zweeffilm() {
  const scherm = useKiosk((s) => s.scherm)
  const scene = useScene((s) => s.scene)
  const profiel = useScene((s) => s.profiel)
  const profielBron = useScene((s) => s.profielBron)
  const gemetenFps = useScene((s) => s.gemetenFps)
  const paneelOpen = useScene((s) => s.paneelOpen)
  const zetHalStil = useZweeffilm((s) => s.zetHalStil)

  const [ontbreekt, zetOntbreekt] = useState(false)
  const [geblokkeerd, zetGeblokkeerd] = useState(false)
  const [verhouding, zetVerhouding] = useState(() => window.innerWidth / window.innerHeight)
  useEffect(() => {
    const opMaat = () => zetVerhouding(window.innerWidth / window.innerHeight)
    window.addEventListener('resize', opMaat)
    return () => window.removeEventListener('resize', opMaat)
  }, [])

  const aan = (PROEF.film ?? scene.objecten.zweeffilm) && !PROEF.cam && PROEF.tijd === null
  const linde = PROEF.linde ?? scene.objecten.lindeBeeld
  const past = Math.abs(verhouding - FILM_VERHOUDING) < 0.02
  const laden = aan && past && !ontbreekt
  // De prestatiemeting bij het opstarten (Prestatie.tsx) meet de hal; met de
  // film eroverheen zou hij een video meten.
  const metingKlaar = profiel !== 'vol' || profielBron === 'handmatig' || gemetenFps !== null
  const wil = laden && !geblokkeerd && metingKlaar && !paneelOpen && scherm === 'hoofdscherm'
    && halZoalsFilm(scene, profiel, linde)

  const video = useRef<HTMLVideoElement>(null)
  const wilRef = useRef(wil)
  wilRef.current = wil

  useEffect(() => {
    const v = video.current
    if (!laden || !v) return
    let fase: Fase = 'hal'
    // Op de wortel van het document, zoals `data-scene`: zo ziet een
    // afdrukscript of iemand in DevTools waar de overname staat.
    const zet = (f: Fase) => { fase = f; document.documentElement.dataset.zweeffilm = f }
    zet('hal')
    let doel = 0
    let sinds = 0
    let halBeeldenBij = 0
    let raf = 0

    const toon = (zichtbaar: boolean, ms: number) => {
      v.style.transition = `opacity ${ms}ms linear`
      v.style.opacity = zichtbaar ? '1' : '0'
    }
    const volgFilm = () => { halklok.t = v.currentTime }
    // De dekking zoals hij nu in beeld staat. Niet de klok: op een drukke
    // pc begint de overgang pas een beeld later dan hij gezet is, en de hal
    // mag pas stilstaan als de film hem echt bedekt.
    const dekking = () => Number(getComputedStyle(v).opacity)

    const naarHal = () => {
      volgFilm()
      halklok.extern = true
      zweefStatus.springNaarRoute = true
      zweefStatus.vasthouden = true
      zetHalStil(false)
      halBeeldenBij = hal.beelden
      sinds = 0
      zet('naarHal')
    }

    const stap = (nu: number) => {
      raf = requestAnimationFrame(stap)
      const wilNu = wilRef.current
      switch (fase) {
        case 'hal': {
          if (!wilNu || !zweefStatus.opRoute || v.readyState < HTMLMediaElement.HAVE_METADATA) break
          doel = (halklok.t + VOORUIT) % OMLOOP
          v.currentTime = doel
          zet('zoeken')
          break
        }
        case 'zoeken': {
          if (!wilNu || !zweefStatus.opRoute) { zet('hal'); break }
          if (v.seeking || v.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) break
          const nog = (((doel - halklok.t) % OMLOOP) + OMLOOP) % OMLOOP
          // Voorbij: het zoeken duurde langer dan VOORUIT. Opnieuw, verder vooruit.
          if (nog > OMLOOP - 1) { zet('hal'); break }
          if (nog > 1 / 120) break
          halklok.extern = true
          volgFilm()
          v.play().catch(() => { /* gedempt: mag altijd */ })
          toon(true, NAAR_FILM_MS)
          sinds = nu
          zet('naarFilm')
          break
        }
        case 'naarFilm': {
          volgFilm()
          if (!wilNu) { naarHal(); break }
          if (nu - sinds >= NAAR_FILM_MS && dekking() >= 0.999) {
            zetHalStil(true)
            zet('film')
          }
          break
        }
        case 'film': {
          if (!wilNu) naarHal()
          break
        }
        case 'naarHal': {
          volgFilm()
          // De hal heeft twee beelden op de tijd van de film getekend: dan pas
          // wegvloeien, anders vloeit de film over een oud beeld weg.
          if (sinds === 0) {
            if (hal.beelden - halBeeldenBij < 2) break
            toon(false, NAAR_HAL_MS)
            sinds = nu
          }
          if (nu - sinds < NAAR_HAL_MS || dekking() > 0.001) break
          v.pause()
          halklok.extern = false
          zweefStatus.vasthouden = false
          zet('hal')
          break
        }
      }
    }
    raf = requestAnimationFrame(stap)

    // Een film die bleef hangen (het netwerk, zonder offline kopie): terug naar
    // de hal, en een minuut niet opnieuw proberen.
    let hangt = 0
    const wacht = () => {
      if (fase !== 'film' && fase !== 'naarFilm') return
      hangt = window.setTimeout(() => {
        zetGeblokkeerd(true)
        window.setTimeout(() => zetGeblokkeerd(false), BLOKKADE_MS)
      }, 800)
    }
    const speelt = () => window.clearTimeout(hangt)
    v.addEventListener('waiting', wacht)
    v.addEventListener('playing', speelt)

    return () => {
      cancelAnimationFrame(raf)
      window.clearTimeout(hangt)
      v.removeEventListener('waiting', wacht)
      v.removeEventListener('playing', speelt)
      v.pause()
      toon(false, 0)
      halklok.extern = false
      zweefStatus.vasthouden = false
      zetHalStil(false)
      delete document.documentElement.dataset.zweeffilm
    }
  }, [laden, zetHalStil])

  if (!laden) return null
  return (
    <video
      ref={video}
      className="kiosk-zweeffilm"
      src={ZWEEFFILM}
      muted
      loop
      playsInline
      preload="auto"
      disablePictureInPicture
      onError={() => zetOntbreekt(true)}
    />
  )
}
